import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Save,
    AlertCircle,
    CheckCircle2,
    Bell,
    Users,
    Building2,
    MapPin,
    Type,
    Flag,
    FileText,
    Ticket,
    UserCircle,
    Plus,
    Edit2,
    Trash2,
    X,
    Loader2,
    Code,
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    Wrench,
    RefreshCcw,
    Workflow,
    Monitor,
    Eye,
    Info,
    Tag as TagIcon,
    MessageSquare,
    Search
} from 'lucide-react';
import baseapi from '../../services/baseapi';
import SingleSelectLocalSearchApiDropdown from '../../components/SingleSelectLocalSearchApiDropdown';
import SingleSelectLocalSearchIntegrationApiDropdown from '../../components/SingleSelectLocalSearchIntegrationApiDropdown';
import MultiSelectApiDropdown from '../../components/MultiSelectApiDropdown';
import MultiSelectDropdown from '../../components/MultiSelectDropdown';
import { ItemsPerPageDropdown } from '../../components/ItemsPerPageDropdown ';
import { useClientLevel } from '../../hooks/useClientLevel';
import { hasPermission } from '../../utils/permissionsHelper';
import { showSuccess } from '../../utils/toast';
import ColumnVisibilityDropdown, { type ColumnOption } from '../../components/ColumnVisibilityDropdown';

const safeParseJSON = (str: string | null | undefined) => {
    if (!str) return null;
    try {
        return typeof str === 'string' ? JSON.parse(str) : str;
    } catch {
        return null;
    }
};

interface VariableDropdownProps {
    variables: { label: string; value: string }[];
    onSelect: (val: string) => void;
    position?: 'top' | 'bottom';
    disabled?: boolean;
    loading?: boolean;
}

const VariableDropdown = ({ variables, onSelect, position = 'bottom', disabled = false, loading = false }: VariableDropdownProps) => {
    const [open, setOpen] = React.useState(false);
    return (
        <div className="relative">
            <button
                type="button"
                onClick={(e) => { e.preventDefault(); if (!disabled) setOpen(!open); }}
                disabled={disabled}
                className={`text-[10px] bg-slate-800 hover:bg-slate-700 text-gray-300 px-2 py-1 rounded border border-gray-700 flex items-center gap-1 transition-colors group tracking-wide font-bold uppercase ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
                <Code className="w-3 h-3 text-orange-400 group-hover:text-orange-300" />
                <span>{loading ? 'Loading...' : 'Insert Variable'}</span>
            </button>
            {open && !disabled && (
                <>
                    <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
                    <div className={`absolute right-0 w-48 bg-gray-800 border border-gray-700 rounded-lg shadow-2xl z-50 py-1 max-h-[200px] overflow-y-auto custom-scrollbar ${position === 'top' ? 'bottom-full mb-1' : 'mt-1'}`}>
                        {variables.length === 0 ? (
                            <div className="px-3 py-2 text-[11px] text-gray-500 italic">No variables available</div>
                        ) : (
                            variables.map(v => (
                                <button
                                    key={v.value}
                                    type="button"
                                    onClick={(e) => { e.preventDefault(); onSelect(v.value); setOpen(false); }}
                                    className="w-full text-left px-3 py-2 text-[11px] text-gray-300 hover:bg-slate-700 hover:text-white transition-colors flex flex-col gap-0.5 border-b border-gray-700/50 last:border-0"
                                >
                                    <span className="font-semibold text-gray-200">{v.label}</span>
                                    <span className="text-[10px] text-orange-400 font-mono">{v.value}</span>
                                </button>
                            ))
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

const ConfigurationSettings = () => {
    const level = useClientLevel();
    const isGlobal = level?.name === 'global';

    const [searchParams, setSearchParams] = useSearchParams();
    const isIntegrationMissing = false;
    const isVerifyingDomain = false;

    const [configs, setConfigs] = useState<any[]>([]);
    const [fetching, setFetching] = useState(false);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(15);
    const [totalRecords, setTotalRecords] = useState(0);

    // Search state
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
            setCurrentPage(1);
        }, 500);
        return () => clearTimeout(timer);
    }, [search]);

    // Toggle confirmation state
    const [toggleModal, setToggleModal] = useState<{ isOpen: boolean; id: number | null; isActive: boolean }>({
        isOpen: false,
        id: null,
        isActive: false
    });
    const [toggling, setToggling] = useState(false);

    // Delete confirmation state
    const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; id: number | null }>({
        isOpen: false,
        id: null
    });
    const [deleting, setDeleting] = useState(false);

    // Uninstalled integrations warning modal state
    const [uninstalledModal, setUninstalledModal] = useState<{ isOpen: boolean; integrations: string[] }>({
        isOpen: false,
        integrations: []
    });
    const [uninstalledCount, setUninstalledCount] = useState<number>(0);

    // Radio mode states for include/exclude
    const [clientsMode, setClientsMode] = useState<'include' | 'exclude'>('include');
    const [assetsMode, setAssetsMode] = useState<'include' | 'exclude'>('include');
    const [tagsMode, setTagsMode] = useState<'include' | 'exclude'>('include');
    const [previewTemplateCode, setPreviewTemplateCode] = useState<string | null>(null);
    const [currentStep, setCurrentStep] = useState<number>(1);

    const [integrationOptions, setIntegrationOptions] = useState<{ label: string; value: string }[]>([]);
    const [emailIntegrations, setEmailIntegrations] = useState<{ label: string; value: number }[]>([]);

    useEffect(() => {
        const fetchEmailIntegrations = async () => {
            try {
                const res = await baseapi.get('/integrations');
                if (res.data?.status && Array.isArray(res.data.data)) {
                    const options = res.data.data
                        .filter((item: any) => ['smtp', 'google_workspace', 'google-workspace', 'google', 'google_sso', 'google-sso', 'microsoft', 'microsoft-sso', 'microsoft_sso', 'sso'].includes(item.integration_type))
                        .map((item: any) => ({
                            label: item.name || item.integration_type,
                            value: item.id
                        }));
                    setEmailIntegrations(options);
                }
            } catch (error) {
                console.error("Failed to fetch email integrations:", error);
            }
        };
        fetchEmailIntegrations();
    }, []);

    useEffect(() => {
        const fetchIntegrationTypes = async () => {
            try {
                const res = await baseapi.get('/event_trigger_configs/integrations/');
                if (res.data?.status && Array.isArray(res.data.data)) {
                    const options = res.data.data.map((item: any) => ({
                        label: item.name || '',
                        value: item.value || ''
                    }));
                    setIntegrationOptions(options);

                    setFormData(prev => {
                        if (!editingId && (prev.integration_types.length === 0 || prev.integration_types.includes('rundeskpro') || prev.integration_types.includes('ticketing'))) {
                            return { ...prev, integration_types: options.length > 0 ? [options[0].value] : [] };
                        }
                        return prev;
                    });
                }
            } catch (error) {
                console.error("Failed to fetch integration types:", error);
            }
        };
        fetchIntegrationTypes();
    }, [editingId]);

    const [formData, setFormData] = useState({
        event: null as any,
        client: null as any,
        department: null as any,
        company: null as any,
        site: null as any,
        ticketType: null as any,
        user: null as any,
        subject: '',
        priority: null as any,
        description_template: null as any,
        name: '',
        is_active: true,
        include_clients: [] as any[],
        exclude_clients: [] as any[],
        include_tags: [] as any[],
        exclude_tags: [] as any[],
        include_assets: [] as any[],
        exclude_assets: [] as any[],
        integration_types: [] as string[],
        teams_channel: null as any,
        teams_open_message: '' as string,
        teams_close_message: '' as string,
        slack_channel: null as any,
        slack_open_message: '' as string,
        slack_close_message: '' as string,
        email_integration_id: null as any,
        recipient_emails: '',
        sender_email: '',
        email_subject: '',
        email_template: null as any,
        rundesk_enabled: true,
        teams_enabled: true,
        slack_enabled: true,
        email_enabled: true,
    });

    const [status, setStatus] = useState<{ type: 'success' | 'error' | null; message: string }>({
        type: null,
        message: ''
    });

    const [loading, setLoading] = useState(false);
    const [fetchingDetails, setFetchingDetails] = useState(false);
    const canCreate = hasPermission('ticketing', 'create');
    const canUpdate = hasPermission('ticketing', 'update');
    const canDelete = hasPermission('ticketing', 'delete');

    // Column visibility state
    const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>({
        event_type: true,
        name: true,
        integrations: true,
        status: true,
        actions: true,
    });

    const columnOptions: ColumnOption<string>[] = useMemo(() => [
        { key: 'event_type', label: 'Event Type' },
        { key: 'name', label: 'Rule Name' },
        { key: 'integrations', label: 'Integrations' },
        { key: 'status', label: 'Status' },
        ...((canUpdate || canDelete) ? [{ key: 'actions', label: 'Actions' }] : []),
    ], [canUpdate, canDelete]);

    const visibleColsCount = useMemo(() => {
        let count = 0;
        if (visibleColumns.event_type) count++;
        if (visibleColumns.name) count++;
        if (visibleColumns.integrations) count++;
        if (visibleColumns.status) count++;
        if ((canUpdate || canDelete) && visibleColumns.actions) count++;
        return count || 1;
    }, [visibleColumns, canUpdate, canDelete]);

    const [variables, setVariables] = useState<Record<string, { label: string; value: string }[]>>({});
    const [loadingVariables, setLoadingVariables] = useState(false);

    useEffect(() => {
        if (!isModalOpen) {
            setVariables({});
            return;
        }

        const fetchVariables = async () => {
            const eventType = formData.event;
            if (!eventType) {
                setVariables({});
                return;
            }

            const currentEventType = typeof eventType === 'string' ? eventType : eventType.event_type;
            if (!currentEventType) {
                setVariables({});
                return;
            }

            setLoadingVariables(true);
            try {
                let isCustom = eventType.is_custom;
                let moduleName = eventType.module;

                // If is_custom is undefined, fetch event types to find the match
                if (typeof isCustom === 'undefined') {
                    const typesResponse = await baseapi.get('/events/types', {
                        params: !isGlobal ? { client_id: level?.id } : {}
                    });
                    if (typesResponse.data.status && Array.isArray(typesResponse.data.data)) {
                        const found = typesResponse.data.data.find(
                            (t: any) => t.event_type === currentEventType
                        );
                        if (found) {
                            isCustom = found.is_custom;
                            moduleName = found.module;
                        }
                    }
                }

                // Now we fetch template variables
                const params: any = {
                    is_custom: isCustom ?? false,
                    page: "configurations",
                    integration_type: JSON.stringify(formData.integration_types)
                };
                if (isCustom && moduleName) {
                    params.module_name = moduleName;
                }

                const varResponse = await baseapi.get('/events/template-variables', { params });
                if (varResponse.data.status && varResponse.data.data && typeof varResponse.data.data === 'object') {
                    setVariables(varResponse.data.data);
                } else {
                    setVariables({});
                }
            } catch (error) {
                console.error("Error fetching template variables:", error);
                setVariables({});
            } finally {
                setLoadingVariables(false);
            }
        };

        fetchVariables();
    }, [formData.event, isModalOpen, isGlobal, level?.id, formData.integration_types]);

    const activeSteps = useMemo(() => {
        const steps: string[] = [];
        if (formData.integration_types.includes('rundesk') || formData.integration_types.includes('rundeskpro') || formData.integration_types.includes('ticketing')) steps.push('rundesk');
        if (formData.integration_types.includes('microsoft_teams') || formData.integration_types.includes('teams')) steps.push('microsoft_teams');
        if (formData.integration_types.includes('slack')) steps.push('slack');
        if (formData.integration_types.includes('email') || formData.integration_types.includes('smtp')) steps.push('email');
        return steps;
    }, [formData.integration_types]);

    useEffect(() => {
        if (currentStep > activeSteps.length) {
            setCurrentStep(Math.max(1, activeSteps.length));
        }
    }, [activeSteps.length, currentStep]);

    const activeStep = activeSteps[currentStep - 1] || activeSteps[0] || 'rundesk';
    const showTicketingForm = activeStep === 'rundesk';
    const showTeamsForm = activeStep === 'microsoft_teams' || activeStep === 'teams';
    const showSlackForm = activeStep === 'slack';
    const showEmailForm = activeStep === 'email';

    const fetchConfigs = async () => {
        setFetching(true);
        try {
            const queryObj: any = {};

            const skip = (currentPage - 1) * limit;
            const params: any = {
                limit,
                skip,
            };
            if (!isGlobal) {
                queryObj.client_id = level?.id;
            } else {
                queryObj.is_global = true;
            }
            if (debouncedSearch) {
                queryObj.$or = [
                    { name: { $ilike: `%${debouncedSearch}%` } },
                    { event_type: { $ilike: `%${debouncedSearch}%` } }
                ];
            }
            params.query = JSON.stringify(queryObj);
            const response = await baseapi.get('/event_trigger_configs/', { params });
            if (response.data.status) {
                setConfigs(response.data.data || []);
                setTotalRecords(response.data.total || response.data.data.length);
                setUninstalledCount(response.data.uninstalled_integrations_count || 0);
            }
        } catch (error) {
            console.error("Error fetching configs:", error);
        } finally {
            setFetching(false);
        }
    };

    useEffect(() => {
        if (level && !isVerifyingDomain && !isIntegrationMissing) {
            fetchConfigs();
        }
    }, [level, currentPage, limit, isVerifyingDomain, isIntegrationMissing, debouncedSearch]);



    const handleSelectChange = (name: string, value: any) => {
        setFormData(prev => {
            const newState = { ...prev, [name]: value };
            // If company is changed, clear the site
            if (name === 'company') {
                newState.site = null;
            }
            return newState;
        });
        if (name === 'event' && value) {
            setStatus({ type: null, message: '' });
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (name === 'name' && value.trim()) {
            setStatus({ type: null, message: '' });
        }
    };

    const handleInjectVariable = (field: 'subject' | 'description' | 'ticket_closure_description' | 'teams_open_message' | 'teams_close_message' | 'slack_open_message' | 'slack_close_message' | 'email_subject', variable: string) => {
        setFormData(prev => ({
            ...prev,
            [field]: (prev[field as keyof typeof prev] as string || '') + (prev[field as keyof typeof prev] && !(prev[field as keyof typeof prev] as string).endsWith(' ') ? ' ' : '') + variable
        }));
    };

    const openCreateModal = () => {
        setEditingId(null);
        setClientsMode('include');
        setAssetsMode('include');
        setTagsMode('include');
        setCurrentStep(1);
        setFormData({
            event: null,
            client: null,
            department: null,
            company: null,
            site: null,
            ticketType: null,
            user: null,
            subject: '',
            priority: null,
            description_template: null,
            name: '',
            is_active: true,
            include_clients: [],
            exclude_clients: [],
            include_tags: [],
            exclude_tags: [],
            include_assets: [],
            exclude_assets: [],
            integration_types: integrationOptions.length > 0 ? [integrationOptions[0].value] : [],
            teams_channel: null,
            teams_open_message: '',
            teams_close_message: '',
            slack_channel: null,
            slack_open_message: '',
            slack_close_message: '',
            email_integration_id: null,
            recipient_emails: '',
            sender_email: '',
            email_subject: '',
            email_template: null,
            rundesk_enabled: true,
            teams_enabled: true,
            slack_enabled: true,
            email_enabled: true,
        });
        setStatus({ type: null, message: '' });
        setIsModalOpen(true);
    };

    const openEditModal = async (config: any) => {
        setFetchingDetails(true);
        setEditingId(config.id);
        setCurrentStep(1);

        try {
            const response = await baseapi.get(`/event_trigger_configs/${config.id}/`);
            const fetchedConfig = response.data.status ? response.data.data : config;

            const parentConfig = fetchedConfig.trigger_config || fetchedConfig;
            const ticketConfig = fetchedConfig.rundeskpro_ticket_config || {};
            const notificationConfigs = fetchedConfig.notification_configs || [];

            const eventType = parentConfig.event_type || fetchedConfig.event_type || '';
            const eventObj = eventType ? { event_type: eventType, label: eventType } : null;

            const safeParse = (str: any) => {
                if (!str) return null;
                if (typeof str === 'object') return str;
                try { return JSON.parse(str); } catch { return null; }
            };

            const ticketTitle = ticketConfig.ticket_title || fetchedConfig.ticket_title || '';
            const ticketDepartment = ticketConfig.ticket_department || fetchedConfig.ticket_department;
            const ticketCompany = ticketConfig.ticket_company || fetchedConfig.ticket_company;
            const ticketSite = ticketConfig.ticket_site || fetchedConfig.ticket_site;
            const ticketType = ticketConfig.ticket_type || fetchedConfig.ticket_type;
            const ticketRequestor = ticketConfig.ticket_requestor || fetchedConfig.ticket_requestor;
            const ticketPriority = ticketConfig.ticket_priority || fetchedConfig.ticket_priority;

            let descTemplate = null;
            const descTemplateId = ticketConfig.description_template_id || fetchedConfig.description_template_id;
            if (descTemplateId) {
                descTemplate = {
                    id: descTemplateId,
                    name: ticketConfig.template_name || fetchedConfig.template_name || `Template #${descTemplateId}`,
                    ticket_open_template: ticketConfig.ticket_open_template || fetchedConfig.ticket_open_template || '',
                    ticket_closure_template: ticketConfig.ticket_closure_template || fetchedConfig.ticket_closure_template || ''
                };
            }

            const rawExcludeClients = ticketConfig.exclude_clients || parentConfig.exclude_clients || fetchedConfig.exclude_clients;
            const rawIncludeClients = ticketConfig.include_clients || parentConfig.include_clients || fetchedConfig.include_clients;
            const rawExcludeAssets = ticketConfig.exclude_assets || parentConfig.exclude_assets || fetchedConfig.exclude_assets;
            const rawIncludeAssets = ticketConfig.include_assets || parentConfig.include_assets || fetchedConfig.include_assets;
            const rawExcludeTags = ticketConfig.exclude_tags || parentConfig.exclude_tags || fetchedConfig.exclude_tags;
            const rawIncludeTags = ticketConfig.include_tags || parentConfig.include_tags || fetchedConfig.include_tags;

            const parsedExcludeClients = safeParse(rawExcludeClients) || [];
            const parsedIncludeClients = safeParse(rawIncludeClients) || [];
            const parsedExcludeAssets = safeParse(rawExcludeAssets) || [];
            const parsedIncludeAssets = safeParse(rawIncludeAssets) || [];
            const parsedExcludeTags = safeParse(rawExcludeTags) || [];
            const parsedIncludeTags = safeParse(rawIncludeTags) || [];

            setClientsMode(parsedExcludeClients.length > 0 ? 'exclude' : 'include');
            setAssetsMode(parsedExcludeAssets.length > 0 ? 'exclude' : 'include');
            setTagsMode(parsedExcludeTags.length > 0 ? 'exclude' : 'include');

            // Parse integration types
            let integrationTypes = (safeParse(parentConfig.integration_types || fetchedConfig.integration_types) || []).map((t: string) => {
                if (t === 'ticketing' || t === 'rundeskpro') return 'rundesk';
                return t;
            });
            if (integrationTypes.length === 0) {
                if (fetchedConfig.rundeskpro_ticket_config || fetchedConfig.ticket_title) {
                    integrationTypes.push('rundesk');
                }
                if (Array.isArray(notificationConfigs)) {
                    notificationConfigs.forEach((nc: any) => {
                        if (nc.integration_type) {
                            const typeVal = (nc.integration_type === 'ticketing' || nc.integration_type === 'rundeskpro') ? 'rundesk' : nc.integration_type;
                            if (!integrationTypes.includes(typeVal)) {
                                integrationTypes.push(typeVal);
                            }
                        }
                    });
                }
                if (integrationTypes.length === 0) {
                    integrationTypes = ['rundesk'];
                }
            }

            // Parse Teams
            const teamsConfig = Array.isArray(notificationConfigs)
                ? notificationConfigs.find((nc: any) => nc.integration_type === 'teams' || nc.integration_type === 'microsoft_teams')
                : null;
            const teamsChannel = teamsConfig
                ? { id: teamsConfig.channel_id, name: teamsConfig.channel_name || teamsConfig.channel_id }
                : (safeParse(fetchedConfig.teams_channel) || null);
            const teamsOpenMessage = teamsConfig
                ? (teamsConfig.channel_open_message || '')
                : (fetchedConfig.teams_open_message || '');
            const teamsCloseMessage = teamsConfig
                ? (teamsConfig.channel_close_message || '')
                : (fetchedConfig.teams_close_message || '');

            // Parse Slack
            const slackConfig = Array.isArray(notificationConfigs)
                ? notificationConfigs.find((nc: any) => nc.integration_type === 'slack')
                : null;
            const slackChannel = slackConfig
                ? { id: slackConfig.channel_id, name: slackConfig.channel_name || slackConfig.channel_id }
                : (safeParse(fetchedConfig.slack_channel) || null);
            const slackOpenMessage = slackConfig
                ? (slackConfig.channel_open_message || '')
                : (fetchedConfig.slack_open_message || '');
            const slackCloseMessage = slackConfig
                ? (slackConfig.channel_close_message || '')
                : (fetchedConfig.slack_close_message || '');

            // Parse Email
            const emailConfig = fetchedConfig.email_notification_config;
            const emailIntegrationId = emailConfig ? { id: emailConfig.integration_id, name: `Integration #${emailConfig.integration_id}` } : null;
            const recipientEmails = emailConfig ? (emailConfig.recipient_emails || '') : '';
            const senderEmail = emailConfig ? (emailConfig.sender_email || '') : '';
            const emailSubject = emailConfig ? (emailConfig.email_subject || '') : '';
            const emailEnabled = emailConfig ? (emailConfig.is_active ?? true) : true;
            let emailTemplate = null;
            if (emailConfig && emailConfig.description_template_id) {
                emailTemplate = { id: emailConfig.description_template_id, name: `Template #${emailConfig.description_template_id}` };
            }

            setFormData({
                event: eventObj,
                client: parentConfig.client_id ? { client_id: parentConfig.client_id, display_name: `Client ${parentConfig.client_id}` } : null,
                department: safeParse(ticketDepartment),
                company: safeParse(ticketCompany),
                site: safeParse(ticketSite),
                ticketType: safeParse(ticketType),
                user: safeParse(ticketRequestor),
                subject: ticketTitle,
                priority: safeParse(ticketPriority),
                description_template: descTemplate,
                name: parentConfig.name || '',
                is_active: parentConfig.is_active ?? true,
                include_clients: parsedIncludeClients,
                exclude_clients: parsedExcludeClients,
                include_tags: parsedIncludeTags,
                exclude_tags: parsedExcludeTags,
                include_assets: parsedIncludeAssets,
                exclude_assets: parsedExcludeAssets,
                integration_types: integrationTypes,
                teams_channel: teamsChannel,
                teams_open_message: teamsOpenMessage,
                teams_close_message: teamsCloseMessage,
                slack_channel: slackChannel,
                slack_open_message: slackOpenMessage,
                slack_close_message: slackCloseMessage,
                email_integration_id: emailIntegrationId,
                recipient_emails: recipientEmails,
                sender_email: senderEmail,
                email_subject: emailSubject,
                email_template: emailTemplate,
                rundesk_enabled: ticketConfig.is_active ?? true,
                teams_enabled: teamsConfig ? (teamsConfig.is_active ?? true) : true,
                slack_enabled: slackConfig ? (slackConfig.is_active ?? true) : true,
                email_enabled: emailEnabled,
            });
            setStatus({ type: null, message: '' });
            setIsModalOpen(true);
        } catch (error) {
            console.error("Error fetching config details on edit:", error);
            alert("Failed to fetch configuration details.");
        } finally {
            setFetchingDetails(false);
        }
    };

    const handleDelete = (id: number) => {
        setDeleteModal({ isOpen: true, id });
    };

    const confirmDelete = async () => {
        if (!deleteModal.id) return;
        setDeleting(true);
        try {
            const response = await baseapi.delete(`/event_trigger_configs/${deleteModal.id}/`);
            if (response.data.status) {
                fetchConfigs();
                setDeleteModal({ isOpen: false, id: null });
            } else {
                alert("Failed to delete");
            }
        } catch (error) {
            console.error("Error deleting config:", error);
            alert("Error deleting config");
        } finally {
            setDeleting(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate mandatory fields
        if (!formData.name?.trim()) {
            setStatus({ type: 'error', message: 'Rule Name is a mandatory field.' });
            return;
        }
        if (!formData.event || !formData.event.event_type) {
            setStatus({ type: 'error', message: 'Event Type is a mandatory field.' });
            return;
        }

        if (currentStep < activeSteps.length) {
            setCurrentStep(prev => prev + 1);
            return;
        }

        setLoading(true);
        setStatus({ type: null, message: '' });

        try {
            const getClientId = () => {
                if (!isGlobal) return level?.id;
                if (!formData.client) return null;
                return formData.client.client_id || formData.client.id;
            };

            const includeClientsVal = isGlobal && formData.include_clients.length > 0
                ? JSON.stringify(formData.include_clients.map((c: any) => ({ id: c.id, name: c.name })))
                : null;
            const excludeClientsVal = isGlobal && formData.exclude_clients.length > 0
                ? JSON.stringify(formData.exclude_clients.map((c: any) => ({ id: c.id, name: c.name })))
                : null;
            const includeAssetsVal = !isGlobal && formData.include_assets.length > 0
                ? JSON.stringify(formData.include_assets.map((a: any) => ({ id: a.id, name: a.name })))
                : null;
            const excludeAssetsVal = !isGlobal && formData.exclude_assets.length > 0
                ? JSON.stringify(formData.exclude_assets.map((a: any) => ({ id: a.id, name: a.name })))
                : null;
            const includeTagsVal = formData.include_tags.length > 0
                ? JSON.stringify(formData.include_tags.map((t: any) => ({ id: t.id, name: t.name })))
                : null;
            const excludeTagsVal = formData.exclude_tags.length > 0
                ? JSON.stringify(formData.exclude_tags.map((t: any) => ({ id: t.id, name: t.name })))
                : null;

            const payload: any = {
                trigger_config: {
                    ...(editingId && { id: editingId }),
                    name: formData.name || '',
                    event_type: formData.event?.event_type || '',
                    is_global: isGlobal,
                    is_active: formData.is_active,
                    client_id: getClientId(),
                    integration_types: JSON.stringify(formData.integration_types),
                    include_clients: includeClientsVal,
                    exclude_clients: excludeClientsVal,
                    include_tags: includeTagsVal,
                    exclude_tags: excludeTagsVal,
                    include_assets: includeAssetsVal,
                    exclude_assets: excludeAssetsVal,
                    ticket_title: formData.subject || '',
                    description_template_id: formData.description_template?.id || null,
                    ticket_requestor: formData.user ? JSON.stringify({
                        id: formData.user.id,
                        name: formData.user.name,
                        email: formData.user.email
                    }) : null,
                    ticket_type: formData.ticketType ? JSON.stringify({
                        id: formData.ticketType.id,
                        name: formData.ticketType.name
                    }) : null,
                    ticket_priority: formData.priority ? JSON.stringify({
                        id: formData.priority.id,
                        name: formData.priority.name
                    }) : null,
                    ticket_department: formData.department ? JSON.stringify({
                        id: formData.department.id,
                        name: formData.department.name
                    }) : null,
                    ticket_company: formData.company ? JSON.stringify({
                        id: formData.company.id,
                        name: formData.company.name
                    }) : null,
                    ticket_site: formData.site ? JSON.stringify({
                        id: formData.site.id,
                        name: formData.site.name
                    }) : null,
                    teams_channel: formData.teams_channel ? JSON.stringify(formData.teams_channel) : null,
                    teams_open_message: formData.teams_open_message || '',
                    teams_close_message: formData.teams_close_message || '',
                    slack_channel: formData.slack_channel ? JSON.stringify(formData.slack_channel) : null,
                    slack_open_message: formData.slack_open_message || '',
                    slack_close_message: formData.slack_close_message || ''
                }
            };

            if (formData.integration_types.includes('rundesk')) {
                payload.rundeskpro_ticket_config = {
                    ticket_title: formData.subject || '',
                    description_template_id: formData.description_template?.id || null,
                    ticket_requestor: formData.user ? JSON.stringify({
                        id: formData.user.id,
                        name: formData.user.name,
                        email: formData.user.email
                    }) : null,
                    ticket_type: formData.ticketType ? JSON.stringify({
                        id: formData.ticketType.id,
                        name: formData.ticketType.name
                    }) : null,
                    ticket_priority: formData.priority ? JSON.stringify({
                        id: formData.priority.id,
                        name: formData.priority.name
                    }) : null,
                    ticket_department: formData.department ? JSON.stringify({
                        id: formData.department.id,
                        name: formData.department.name
                    }) : null,
                    ticket_company: formData.company ? JSON.stringify({
                        id: formData.company.id,
                        name: formData.company.name
                    }) : null,
                    ticket_site: formData.site ? JSON.stringify({
                        id: formData.site.id,
                        name: formData.site.name
                    }) : null,
                    include_clients: includeClientsVal,
                    exclude_clients: excludeClientsVal,
                    include_tags: includeTagsVal,
                    exclude_tags: excludeTagsVal,
                    include_assets: includeAssetsVal,
                    exclude_assets: excludeAssetsVal,
                    is_active: formData.rundesk_enabled
                };
            } else {
                payload.rundeskpro_ticket_config = null;
            }

            const notificationConfigs: any[] = [];

            if (formData.integration_types.includes('microsoft_teams') || formData.integration_types.includes('teams')) {
                notificationConfigs.push({
                    integration_type: 'microsoft_teams',
                    channel_id: formData.teams_channel?.id || '',
                    channel_name: formData.teams_channel?.name || '',
                    channel_open_message: formData.teams_open_message || '',
                    channel_close_message: formData.teams_close_message || '',
                    is_active: formData.teams_enabled
                });
            }

            if (formData.integration_types.includes('slack')) {
                notificationConfigs.push({
                    integration_type: 'slack',
                    channel_id: formData.slack_channel?.id || '',
                    channel_name: formData.slack_channel?.name || '',
                    channel_open_message: formData.slack_open_message || '',
                    channel_close_message: formData.slack_close_message || '',
                    is_active: formData.slack_enabled
                });
            }

            payload.notification_configs = notificationConfigs;

            if (formData.integration_types.includes('email') || formData.integration_types.includes('smtp')) {
                payload.email_notification_config = {
                    integration_id: formData.email_integration_id?.id || formData.email_integration_id?.value || (formData.email_integration_id ? parseInt(formData.email_integration_id as string, 10) : null),
                    sender_email: formData.sender_email || '',
                    recipient_emails: formData.recipient_emails || '',
                    email_subject: formData.email_subject || '',
                    description_template_id: formData.email_template?.id || null,
                    is_active: formData.email_enabled
                };
            } else {
                payload.email_notification_config = null;
            }

            let response;
            if (editingId) {
                response = await baseapi.put(`/event_trigger_configs/${editingId}/`, payload);
            } else {
                response = await baseapi.post('/event_trigger_configs/', payload);
            }

            if (response.data.status) {
                showSuccess("Configuration saved successfully!");
                setIsModalOpen(false);
                fetchConfigs();
            } else {
                throw new Error(response.data.message || 'Failed to save');
            }
        } catch (error: any) {
            setStatus({
                type: 'error',
                message: error.message || 'Failed to save configuration.'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleToggleActive = (config: any) => {
        setToggleModal({
            isOpen: true,
            id: config.id,
            isActive: !(config.is_active ?? config.trigger_config?.is_active ?? true)
        });
    };

    const confirmToggle = async () => {
        if (!toggleModal.id) return;
        setToggling(true);
        try {
            const configObj = configs.find(c => c.id === toggleModal.id);
            const triggerConfig = configObj?.trigger_config || {};
            const rawIntegrationTypes = triggerConfig.integration_types || configObj?.integration_types;
            let parsedIntegrationTypes: string[] = [];
            if (rawIntegrationTypes) {
                if (typeof rawIntegrationTypes === 'string') {
                    try { parsedIntegrationTypes = JSON.parse(rawIntegrationTypes) || []; } catch { }
                } else if (Array.isArray(rawIntegrationTypes)) {
                    parsedIntegrationTypes = rawIntegrationTypes;
                }
            }
            const integrationTypesMapped = parsedIntegrationTypes.map((t: string) =>
                (t === 'ticketing' || t === 'rundeskpro') ? 'rundesk' : t
            );

            const payload = {
                trigger_config: {
                    name: configObj?.name || triggerConfig.name || '',
                    event_type: configObj?.event_type || triggerConfig.event_type || '',
                    is_global: configObj?.is_global ?? triggerConfig.is_global ?? isGlobal,
                    is_active: toggleModal.isActive,
                    client_id: configObj?.client_id || triggerConfig.client_id || null,
                    integration_types: integrationTypesMapped
                }
            };
            const response = await baseapi.put(`/event_trigger_configs/${toggleModal.id}/`, payload);
            if (response.data.status) {
                fetchConfigs();
                setToggleModal({ isOpen: false, id: null, isActive: false });
            } else {
                alert("Failed to update status");
            }
        } catch (error) {
            console.error("Error toggling status:", error);
            alert("Error updating status");
        } finally {
            setToggling(false);
        }
    };

    const labelStyle = "flex items-center gap-1.5 text-[10px] font-bold text-white uppercase tracking-wider mb-0.5";
    const inputStyle = "w-full bg-slate-900 border border-gray-700 rounded-lg px-3 py-3 text-[13px] text-white focus:border-orange-500 focus:bg-gray-800 outline-none transition-all placeholder:text-gray-500 shadow-md";

    return (
        <div className="p-4 animate-fade-in relative h-[calc(100vh-120px)] flex flex-col overflow-hidden">
            <div className="flex flex-col h-full">
                <div className="flex justify-between items-center mb-3">
                    <div className="flex items-center gap-1.5">
                        <div className="bg-orange-600 p-1.5 rounded shadow-lg">
                            <Wrench className="w-4 h-4 text-white" />
                        </div>
                        <div>
                            <h1 className="font-bold text-lg text-gray-200 leading-none">Event Configuration</h1>
                        </div>
                        {!fetching && totalRecords !== undefined && (
                            <div className="font-bold text-lg text-gray-100">
                                ({totalRecords})
                            </div>
                        )}
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={fetchConfigs}
                            className="rounded-lg bg-gray-800 border border-gray-700 text-gray-200 p-2 hover:bg-gray-700 transition-colors"
                            title="Refresh"
                        >
                            <RefreshCcw className={`w-3.5 h-3.5 ${fetching ? 'animate-spin' : ''}`} />
                        </button>
                        <div className="relative w-full sm:w-56">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
                            <input
                                type="text"
                                placeholder="Search rules..."
                                className="w-full bg-gray-800/50 border border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-[11px] text-white focus:border-orange-500 outline-none transition-all"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                        <ColumnVisibilityDropdown
                            columns={columnOptions}
                            visibleColumns={visibleColumns}
                            onChange={setVisibleColumns}
                        />
                        {canCreate && (
                            <button
                                onClick={openCreateModal}
                                className="flex items-center gap-2 px-3 py-1.5 bg-orange-600 hover:bg-orange-500 text-white rounded-lg font-bold text-[11px] transition-colors shadow-lg shadow-orange-900/20"
                            >
                                <Plus className="w-3.5 h-3.5" />
                                Create Rule
                            </button>
                        )}
                    </div>
                </div>

                {uninstalledCount > 0 && (
                    <div className="mb-3 bg-red-500/10 border border-red-500/20 rounded-xl p-3 flex items-center gap-3 text-xs font-semibold text-red-400 animate-in fade-in duration-200">
                        <div className="bg-red-500/20 p-1.5 rounded-lg border border-red-500/30 shrink-0">
                            <AlertCircle size={16} className="text-red-400 animate-pulse" />
                        </div>
                        <div className="flex-1">
                            <span className="font-bold">{uninstalledCount} {uninstalledCount === 1 ? 'rule has' : 'rules have'} uninstalled integrations.</span> Please reinstall the integrations, or if already reinstalled, configure the rules again.
                        </div>
                    </div>
                )}

                <div className="bg-gray-900/40 border border-gray-800 rounded-lg overflow-hidden shadow-2xl backdrop-blur-md flex flex-col min-h-0">
                    <div className="relative overflow-x-auto custom-scroll">
                        <table className="w-full min-w-[1200px] text-left text-xs text-gray-100 border-collapse">
                            <thead className="bg-gray-800/80 sticky top-0 z-10">
                                <tr className="border-b border-gray-700/50">
                                    {visibleColumns.event_type && (
                                        <th className="px-3 py-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest whitespace-nowrap w-40">
                                            <div className="flex items-center gap-2"><Bell size={14} className="text-orange-400" /> Event Type</div>
                                        </th>
                                    )}
                                    {visibleColumns.name && (
                                        <th className="px-3 py-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest whitespace-nowrap w-auto">
                                            <div className="flex items-center gap-2"><Type size={14} className="text-orange-400" /> Rule Name</div>
                                        </th>
                                    )}
                                    {visibleColumns.integrations && (
                                        <th className="px-3 py-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest whitespace-nowrap w-auto">
                                            <div className="flex items-center gap-2"><Workflow size={14} className="text-orange-400" /> Integrations</div>
                                        </th>
                                    )}
                                    {visibleColumns.status && (
                                        <th className="px-3 py-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest whitespace-nowrap w-24">Status</th>
                                    )}
                                    {(canUpdate || canDelete) && visibleColumns.actions && (
                                        <th className="px-3 py-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest whitespace-nowrap w-24 text-right sticky right-0 bg-gray-800 z-20">Actions</th>
                                    )}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-700/60 bg-gray-900/20 text-xs">
                                {fetching || isVerifyingDomain ? (
                                    <tr>
                                        <td colSpan={visibleColsCount} className="px-4 py-8 text-center text-gray-500">
                                            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-orange-500" />
                                            {isVerifyingDomain ? 'Verifying integration...' : 'Loading configurations...'}
                                        </td>
                                    </tr>
                                ) : configs.length === 0 ? (
                                    <tr>
                                        <td colSpan={visibleColsCount} className="px-4 py-8 text-center text-gray-500">
                                            No configurations found.
                                        </td>
                                    </tr>
                                ) : (
                                    configs.map((config) => {
                                        const parentConfig = config.trigger_config || config;
                                        const isConfigActive = parentConfig.is_active ?? config.is_active ?? true;

                                        const rawIntegrations = safeParseJSON(parentConfig.integrations || config.integrations || parentConfig.integration_types || config.integration_types) || [];
                                        const integrationTypes = Array.isArray(rawIntegrations) ? rawIntegrations.map((t: string) => {
                                            if (t === 'ticketing' || t === 'rundeskpro') return 'rundesk';
                                            return t;
                                        }) : [];

                                        if (integrationTypes.length === 0) {
                                            const ticketConfig = config.rundeskpro_ticket_config || {};
                                            if (Object.keys(ticketConfig).length > 0 || config.ticket_title) {
                                                integrationTypes.push('rundesk');
                                            }
                                            const notificationConfigs = config.notification_configs || [];
                                            if (Array.isArray(notificationConfigs)) {
                                                notificationConfigs.forEach((nc: any) => {
                                                    if (nc.integration_type) {
                                                        const typeVal = (nc.integration_type === 'ticketing' || nc.integration_type === 'rundeskpro') ? 'rundesk' : nc.integration_type;
                                                        if (!integrationTypes.includes(typeVal)) {
                                                            integrationTypes.push(typeVal);
                                                        }
                                                    }
                                                });
                                            }
                                        }

                                        const uninstalledIntegrations = config.uninstalled_integrations || parentConfig.uninstalled_integrations || [];
                                        const hasUninstalled = Array.isArray(uninstalledIntegrations) && uninstalledIntegrations.length > 0;

                                        return (
                                            <tr
                                                key={config.id}
                                                onClick={() => {
                                                    searchParams.set('tab', 'config_details');
                                                    searchParams.set('id', config.id.toString());
                                                    setSearchParams(searchParams);
                                                }}
                                                className="hover:bg-gray-800/80 transition-all group border-b border-gray-800/50 cursor-pointer active:bg-gray-800"
                                            >
                                                {visibleColumns.event_type && (
                                                    <td className="px-3 py-2 whitespace-nowrap">
                                                        <div className="flex items-center gap-1.5">
                                                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-gray-800 text-indigo-400 border border-gray-700 uppercase tracking-wider">
                                                                {(parentConfig.event_type || config.event_type || '').replace(/_/g, ' ') || '-'}
                                                            </span>
                                                            {hasUninstalled && (
                                                                <button
                                                                    type="button"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setUninstalledModal({
                                                                            isOpen: true,
                                                                            integrations: uninstalledIntegrations
                                                                        });
                                                                    }}
                                                                    className="text-red-500 hover:text-red-400 animate-pulse focus:outline-none p-0.5 flex items-center"
                                                                    title="Uninstalled Integrations Warning"
                                                                >
                                                                    <Info className="w-3.5 h-3.5" />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                )}
                                                {visibleColumns.name && (
                                                    <td className="px-3 py-2 whitespace-nowrap max-w-[150px] truncate text-[11px] font-bold text-gray-200 tracking-wide" title={parentConfig.name || config.name}>
                                                        {parentConfig.name || config.name || '-'}
                                                    </td>
                                                )}
                                                {visibleColumns.integrations && (
                                                    <td className="px-3 py-2 whitespace-nowrap">
                                                        <div className="flex items-center gap-1.5 flex-wrap max-w-[200px]">
                                                            {integrationTypes.length > 0 ? integrationTypes.map((type: string, idx: number) => (
                                                                <span key={idx} className="px-2 py-0.5 bg-gray-800 border border-gray-700 rounded text-[9px] font-bold text-gray-300 uppercase tracking-wider">
                                                                    {type === 'rundesk' ? 'Rundesk' : type === 'microsoft_teams' || type === 'teams' ? 'Teams' : type}
                                                                </span>
                                                            )) : (
                                                                <span className="text-[10px] text-gray-500">-</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                )}
                                                {visibleColumns.status && (
                                                    <td className="px-3 py-2 whitespace-nowrap">
                                                        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleToggleActive(config);
                                                                }}
                                                                className={`relative inline-flex h-4 w-8 flex-shrink-0 cursor-pointer items-center rounded-full transition-colors duration-200 outline-none focus:ring-1 focus:ring-green-500/30 ${isConfigActive ? 'bg-[#2E8B57]' : 'bg-[#B22222]'
                                                                    }`}
                                                            >
                                                                <span
                                                                    className={`inline-block h-3 w-3 transform rounded-full bg-[#f8f9fa] shadow-sm transition-transform duration-200 ${isConfigActive ? 'translate-x-[18px]' : 'translate-x-1'
                                                                        }`}
                                                                />
                                                            </button>
                                                            <span className={`text-[9px] font-bold uppercase tracking-widest ${isConfigActive ? 'text-[#3CB371]' : 'text-[#CD5C5C]'}`}>
                                                                {isConfigActive ? 'Active' : 'Inactive'}
                                                            </span>
                                                        </div>
                                                    </td>
                                                )}
                                                {(canUpdate || canDelete) && visibleColumns.actions && (
                                                    <td className="px-3 py-2 whitespace-nowrap text-right sticky right-0 bg-gray-900 z-20" onClick={(e) => e.stopPropagation()}>
                                                        <div className="flex items-center justify-end gap-1.5 grayscale hover:grayscale-0 transition-all">
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    searchParams.set('tab', 'config_details');
                                                                    searchParams.set('id', config.id.toString());
                                                                    setSearchParams(searchParams);
                                                                }}
                                                                className="p-1 px-1.5 bg-indigo-600/10 hover:bg-indigo-600/30 text-indigo-400 rounded-md transition-all border border-indigo-500/10 hover:border-indigo-500/30 flex items-center gap-1.5"
                                                                title="View Rule Details"
                                                            >
                                                                <Info className="w-3.5 h-3.5" />
                                                                <span className="text-[9px] font-black uppercase tracking-tighter">Details</span>
                                                            </button>
                                                            {canUpdate && (
                                                                <button
                                                                    disabled={fetchingDetails}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        openEditModal(config);
                                                                    }}
                                                                    className={`p-1 rounded-md transition-colors ${fetchingDetails ? 'opacity-50 cursor-not-allowed text-gray-600' : 'text-gray-400 hover:text-orange-400 hover:bg-gray-800'}`}
                                                                    title="Edit configuration"
                                                                >
                                                                    <Edit2 className="w-3.5 h-3.5" />
                                                                </button>
                                                            )}
                                                            {canDelete && (
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleDelete(config.id);
                                                                    }}
                                                                    className="p-1 text-red-400 hover:text-red-400 hover:bg-gray-800 rounded-md transition-colors"
                                                                    title="Delete configuration"
                                                                >
                                                                    <Trash2 className="w-3.5 h-3.5" />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                )}
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Footer */}
                    <div className="flex items-center justify-between px-3 py-1.5 bg-gray-800/40 border-t border-gray-700 shrink-0">
                        <div className="flex items-center gap-3">
                            <ItemsPerPageDropdown
                                value={limit}
                                onChange={(v) => {
                                    setLimit(v);
                                    setCurrentPage(1);
                                }}
                            />
                            <span className="text-[9px] font-medium text-gray-500 tracking-widest">
                                <span className="text-gray-300">
                                    {totalRecords === 0 ? 0 : (currentPage - 1) * limit + 1}
                                </span>
                                {" - "}
                                <span className="text-gray-300">
                                    {Math.min(currentPage * limit, totalRecords)}
                                </span>
                                <span className="mx-1 text-gray-600">/</span>
                                <span className="text-gray-300 font-bold">{totalRecords}</span>
                            </span>
                        </div>

                        <div className="flex items-center gap-1">
                            <button
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(1)}
                                className="p-1.5 text-gray-500 hover:text-white disabled:opacity-30"
                            >
                                <ChevronsLeft className='w-4 h-4' />
                            </button>
                            <button
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                className="p-1.5 text-gray-500 hover:text-white disabled:opacity-30"
                            >
                                <ChevronLeft className='w-4 h-4' />
                            </button>
                            <div className="px-3 py-1 bg-orange-600 rounded-lg text-white text-[11px] font-bold mx-1.5 shadow-lg shadow-orange-900/20">
                                {currentPage}
                            </div>
                            <button
                                disabled={currentPage * limit >= totalRecords}
                                onClick={() => setCurrentPage(prev => prev + 1)}
                                className="p-1.5 text-gray-500 hover:text-white disabled:opacity-30"
                            >
                                <ChevronRight className='w-4 h-4' />
                            </button>
                            <button
                                disabled={currentPage * limit >= totalRecords}
                                onClick={() => setCurrentPage(Math.ceil(totalRecords / limit))}
                                className="p-1.5 text-gray-500 hover:text-white disabled:opacity-30"
                            >
                                <ChevronsRight className='w-4 h-4' />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal */}
            {
                isModalOpen && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
                        <div className="bg-gray-900 border border-gray-800 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                            <div className="px-6 py-4 border-b border-gray-800 flex items-center justify-between bg-gray-900/50">
                                <h3 className="text-lg font-bold text-white">
                                    {editingId ? 'Edit Configuration' : 'Create Configuration'}
                                </h3>
                                <button
                                    onClick={() => setIsModalOpen(false)}
                                    className="p-1 text-gray-400 hover:text-white hover:bg-gray-800 rounded-md transition-colors"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="p-6 overflow-y-auto custom-scrollbar flex-1">
                                <form id="config-form" onSubmit={handleSubmit} className="space-y-5">
                                    {/* Common Top Section */}
                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 border-b border-gray-800 pb-5">
                                        {/* Rule Name Input */}
                                        <div className="space-y-2">
                                            <label className={labelStyle}>
                                                <Type size={14} className="text-orange-400 animate-pulse" />
                                                Rule Name <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                placeholder="Rule name..."
                                                className={inputStyle}
                                                required
                                            />
                                        </div>

                                        {/* Event Dropdown (Common to both) */}
                                        <div className="relative z-[80]">
                                            <SingleSelectLocalSearchApiDropdown
                                                label="Event Type"
                                                icon={Bell}
                                                required={true}
                                                apiEndpoint="/events/types"
                                                displayProperty="label"
                                                idProperty="event_type"
                                                searchFields={['label', 'event_type']}
                                                value={formData.event}
                                                onChange={(val) => {
                                                    const newVal = val?.event_type;
                                                    const oldVal = formData.event?.event_type;
                                                    if (newVal !== oldVal) {
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            event: val,
                                                            subject: '',
                                                            description_template: null,
                                                            teams_open_message: '',
                                                            teams_close_message: '',
                                                            slack_open_message: '',
                                                            slack_close_message: '',
                                                            email_subject: '',
                                                            email_template: null
                                                        }));
                                                    } else {
                                                        handleSelectChange('event', val);
                                                    }
                                                    if (val) {
                                                        setStatus({ type: null, message: '' });
                                                    }
                                                }}
                                                additionalParams={!isGlobal ? { client_id: level?.id } : {}}
                                                placeholder="Event..."
                                                searchInputPlaceholder="Search..."
                                            />
                                        </div>

                                        {/* Integration Type Multiselect */}
                                        <div className="relative z-[90]">
                                            <MultiSelectDropdown
                                                label="Integration Type"
                                                icon={Wrench}
                                                options={integrationOptions}
                                                value={formData.integration_types}
                                                onChange={(values) => {
                                                    if (values.length === 0) return; // Enforce at least one channel
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        integration_types: values
                                                    }));
                                                    setCurrentStep(1); // Reset step when selection changes
                                                }}
                                                placeholder="Select Integration Type..."
                                                variant="config"
                                            />
                                        </div>

                                        {/* Status Toggle (Enable Rule) */}
                                        <div className="flex flex-col justify-end">
                                            <label className="flex items-center gap-1.5 text-[10px] font-bold text-white uppercase tracking-wider mb-2">
                                                Active Status
                                            </label>
                                            <div className="flex items-center gap-3 bg-slate-900 border border-gray-700 h-[42px] px-3 rounded-lg">
                                                <span className="text-[11px] font-bold text-gray-400">Enable Rule:</span>
                                                <button
                                                    type="button"
                                                    onClick={() => setFormData(prev => ({ ...prev, is_active: !prev.is_active }))}
                                                    className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${formData.is_active ? 'bg-orange-600' : 'bg-gray-700'
                                                        }`}
                                                >
                                                    <span
                                                        className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${formData.is_active ? 'translate-x-4' : 'translate-x-0'
                                                            }`}
                                                    />
                                                </button>
                                                <span className={`text-[11px] font-bold ${formData.is_active ? 'text-orange-500' : 'text-gray-500'}`}>
                                                    {formData.is_active ? 'Active' : 'Inactive'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Stepper Header */}
                                    {activeSteps.length > 1 && (
                                        <div className="flex items-center justify-between bg-slate-800/40 p-4 rounded-xl border border-gray-800/50 mb-5 shrink-0">
                                            <div className="flex items-center gap-3 flex-1 justify-center">
                                                {activeSteps.map((step, idx) => {
                                                    const stepNum = idx + 1;
                                                    const isStepActive = currentStep === stepNum;
                                                    const stepLabel = step.charAt(0).toUpperCase() + step.slice(1);
                                                    return (
                                                        <React.Fragment key={step}>
                                                            {idx > 0 && (
                                                                <div className={`h-[2px] w-16 md:w-32 transition-colors ${currentStep >= stepNum ? 'bg-orange-600' : 'bg-gray-800'
                                                                    }`} />
                                                            )}
                                                            <button
                                                                type="button"
                                                                onClick={() => setCurrentStep(stepNum)}
                                                                className="flex items-center gap-2 group focus:outline-none cursor-pointer"
                                                            >
                                                                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs border transition-all ${isStepActive
                                                                    ? 'bg-orange-600 text-white border-orange-500 shadow-lg shadow-orange-900/30 scale-105'
                                                                    : currentStep > stepNum
                                                                        ? 'bg-orange-600/30 text-orange-400 border-orange-500/30'
                                                                        : 'bg-slate-900 text-gray-400 border-gray-700 group-hover:border-gray-500'
                                                                    }`}>
                                                                    {stepNum}
                                                                </div>
                                                                <span className={`text-[11px] font-black uppercase tracking-wider transition-colors ${isStepActive ? 'text-orange-500' : 'text-gray-400 group-hover:text-gray-200'
                                                                    }`}>
                                                                    {stepLabel}
                                                                </span>
                                                            </button>
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {/* Ticketing Step */}
                                    {showTicketingForm && (
                                        <div className="space-y-5 animate-in fade-in duration-300">
                                            {/* Enable Ticketing Integration Toggle */}
                                            <div className="flex items-center gap-3 bg-slate-800/30 p-3 rounded-lg border border-gray-800/50">
                                                <span className="text-xs font-bold text-gray-300">Enable Ticketing Integration:</span>
                                                <button
                                                    type="button"
                                                    onClick={() => setFormData(prev => ({ ...prev, rundesk_enabled: !prev.rundesk_enabled }))}
                                                    className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${formData.rundesk_enabled ? 'bg-emerald-600' : 'bg-gray-700'
                                                        }`}
                                                >
                                                    <span
                                                        className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${formData.rundesk_enabled ? 'translate-x-4' : 'translate-x-0'
                                                            }`}
                                                    />
                                                </button>
                                                <span className={`text-[11px] font-bold ${formData.rundesk_enabled ? 'text-emerald-500' : 'text-gray-500'}`}>
                                                    {formData.rundesk_enabled ? 'Enabled' : 'Disabled'}
                                                </span>
                                            </div>

                                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-4">
                                                {/* Department Dropdown */}
                                                <SingleSelectLocalSearchApiDropdown
                                                    label="Department"
                                                    icon={Users}
                                                    apiEndpoint="/ticketing/metadata/departments"
                                                    displayProperty="name"
                                                    searchFields={['name']}
                                                    value={formData.department}
                                                    additionalParams={level.id ? { client_id: level.id } : {}}
                                                    onChange={(val) => handleSelectChange('department', val)}
                                                    placeholder="Department..."
                                                    searchInputPlaceholder="Search..."
                                                />

                                                {/* Company Dropdown */}
                                                <SingleSelectLocalSearchApiDropdown
                                                    label="Company"
                                                    icon={Building2}
                                                    apiEndpoint="/ticketing/metadata/companies"
                                                    displayProperty="name"
                                                    searchFields={['name']}
                                                    value={formData.company}
                                                    additionalParams={level.id ? { client_id: level.id } : {}}
                                                    onChange={(val) => handleSelectChange('company', val)}
                                                    placeholder="Company..."
                                                    searchInputPlaceholder="Search..."
                                                />

                                                {/* Site Dropdown */}
                                                <SingleSelectLocalSearchApiDropdown
                                                    label="Site"
                                                    icon={MapPin}
                                                    apiEndpoint="/ticketing/metadata/sites"
                                                    displayProperty="name"
                                                    searchFields={['name']}
                                                    value={formData.site}
                                                    onChange={(val) => handleSelectChange('site', val)}
                                                    additionalParams={level.id ? { client_id: level.id } : {}}
                                                    placeholder={formData.company ? "Select Site..." : "Select Company first..."}
                                                    searchInputPlaceholder="Search sites..."
                                                    disabled={!formData.company}
                                                    additionalQueries={{ company_id: formData.company?.id }}
                                                />

                                                {/* Ticket Type Dropdown */}
                                                <SingleSelectLocalSearchApiDropdown
                                                    label="Ticket Type"
                                                    icon={Ticket}
                                                    apiEndpoint="/ticketing/metadata/ticket-types"
                                                    displayProperty="name"
                                                    searchFields={['name']}
                                                    value={formData.ticketType}
                                                    additionalParams={level.id ? { client_id: level.id } : {}}
                                                    onChange={(val) => handleSelectChange('ticketType', val)}
                                                    placeholder="Ticket Type..."
                                                    searchInputPlaceholder="Search..."
                                                />

                                                {/* Users Dropdown */}
                                                <SingleSelectLocalSearchApiDropdown
                                                    label="Requestor"
                                                    icon={UserCircle}
                                                    apiEndpoint="/ticketing/metadata/users"
                                                    displayProperty="name"
                                                    secondaryDisplayProperty="email"
                                                    searchFields={['name', 'email']}
                                                    value={formData.user}
                                                    onChange={(val) => handleSelectChange('user', val)}
                                                    additionalParams={level.id ? { client_id: level.id } : {}}
                                                    placeholder="Select User..."
                                                    searchInputPlaceholder="Search users..."
                                                />

                                                {/* Priority */}
                                                <SingleSelectLocalSearchApiDropdown
                                                    label="Priority"
                                                    icon={Flag}
                                                    apiEndpoint="/ticketing/metadata/priorities"
                                                    displayProperty="name"
                                                    searchFields={['name']}
                                                    value={formData.priority}
                                                    onChange={(val) => handleSelectChange('priority', val)}
                                                    additionalParams={level.id ? { client_id: level.id } : {}}
                                                    placeholder="Priority..."
                                                    searchInputPlaceholder="Search..."
                                                />
                                            </div>

                                            <div className="flex items-center gap-2 px-3 py-2 bg-amber-500/5 border border-amber-500/20 rounded-lg">
                                                <AlertCircle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
                                                <span className="text-[10px] text-amber-300/80 font-medium">
                                                    You can select only <span className="font-bold text-amber-300">Include</span> or <span className="font-bold text-amber-300">Exclude</span> for each filter — not both. Switching will clear the previous selection.
                                                </span>
                                            </div>

                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-slate-800/30 p-4 rounded-xl border border-gray-800/50">
                                                {/* Clients / Assets Filter */}
                                                {isGlobal ? (
                                                    <div className="space-y-2">
                                                        <div className="flex items-center gap-1">
                                                            <label className="flex items-center gap-2 text-xs font-semibold text-gray-300">
                                                                <Users className="w-3 h-3 text-orange-400" /> Clients
                                                            </label>
                                                            <div className="flex items-center bg-gray-900/60 rounded-lg p-0.5 border border-gray-700 ml-auto">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => { setClientsMode('include'); setFormData(prev => ({ ...prev, exclude_clients: [] })); }}
                                                                    className={`px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all ${clientsMode === 'include' ? 'bg-emerald-600/30 text-emerald-400' : 'text-gray-500 hover:text-gray-300'
                                                                        }`}
                                                                >
                                                                    Include
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => { setClientsMode('exclude'); setFormData(prev => ({ ...prev, include_clients: [] })); }}
                                                                    className={`px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all ${clientsMode === 'exclude' ? 'bg-rose-600/30 text-rose-400' : 'text-gray-500 hover:text-gray-300'
                                                                        }`}
                                                                >
                                                                    Exclude
                                                                </button>
                                                            </div>
                                                        </div>
                                                        <MultiSelectApiDropdown
                                                            icon={Users}
                                                            apiEndpoint="/clients"
                                                            displayProperty="name"
                                                            idProperty="id"
                                                            searchFields={['name', 'display_name']}
                                                            value={clientsMode === 'include' ? formData.include_clients : formData.exclude_clients}
                                                            onChange={(val) => handleSelectChange(clientsMode === 'include' ? 'include_clients' : 'exclude_clients', val)}
                                                            placeholder={clientsMode === 'include' ? 'Select clients to include...' : 'Select clients to exclude...'}
                                                            searchInputPlaceholder="Search clients..."
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className="space-y-2">
                                                        <div className="flex items-center gap-1">
                                                            <label className="flex items-center gap-2 text-xs font-semibold text-gray-300">
                                                                <Monitor className="w-3 h-3 text-orange-400" /> Assets
                                                            </label>
                                                            <div className="flex items-center bg-gray-900/60 rounded-lg p-0.5 border border-gray-700 ml-auto">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => { setAssetsMode('include'); setFormData(prev => ({ ...prev, exclude_assets: [] })); }}
                                                                    className={`px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all ${assetsMode === 'include' ? 'bg-emerald-600/30 text-emerald-400' : 'text-gray-500 hover:text-gray-300'
                                                                        }`}
                                                                >
                                                                    Include
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => { setAssetsMode('exclude'); setFormData(prev => ({ ...prev, include_assets: [] })); }}
                                                                    className={`px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all ${assetsMode === 'exclude' ? 'bg-rose-600/30 text-rose-400' : 'text-gray-500 hover:text-gray-300'
                                                                        }`}
                                                                >
                                                                    Exclude
                                                                </button>
                                                            </div>
                                                        </div>
                                                        <MultiSelectApiDropdown
                                                            icon={Monitor}
                                                            apiEndpoint="/assets"
                                                            displayProperty="name"
                                                            idProperty="id"
                                                            searchFields={['name', 'ip_address', 'hostname']}
                                                            value={assetsMode === 'include' ? formData.include_assets : formData.exclude_assets}
                                                            onChange={(val) => handleSelectChange(assetsMode === 'include' ? 'include_assets' : 'exclude_assets', val)}
                                                            placeholder={assetsMode === 'include' ? 'Select assets to include...' : 'Select assets to exclude...'}
                                                            searchInputPlaceholder="Search assets..."
                                                            additionalQueries={{ client_id: level?.id }}
                                                        />
                                                    </div>
                                                )}

                                                {/* Tags Filter */}
                                                <div className="space-y-2">
                                                    <div className="flex items-center gap-1">
                                                        <label className="flex items-center gap-2 text-xs font-semibold text-gray-300">
                                                            <TagIcon className="w-3 h-3 text-orange-400" /> Tags
                                                        </label>
                                                        <div className="flex items-center bg-gray-900/60 rounded-lg p-0.5 border border-gray-700 ml-auto">
                                                            <button
                                                                type="button"
                                                                onClick={() => { setTagsMode('include'); setFormData(prev => ({ ...prev, exclude_tags: [] })); }}
                                                                className={`px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all ${tagsMode === 'include' ? 'bg-emerald-600/30 text-emerald-400' : 'text-gray-500 hover:text-gray-300'
                                                                    }`}
                                                            >
                                                                Include
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => { setTagsMode('exclude'); setFormData(prev => ({ ...prev, include_tags: [] })); }}
                                                                className={`px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all ${tagsMode === 'exclude' ? 'bg-rose-600/30 text-rose-400' : 'text-gray-500 hover:text-gray-300'
                                                                    }`}
                                                            >
                                                                Exclude
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <MultiSelectApiDropdown
                                                        icon={TagIcon}
                                                        apiEndpoint="/tags"
                                                        displayProperty="name"
                                                        idProperty="id"
                                                        searchFields={['name']}
                                                        value={tagsMode === 'include' ? formData.include_tags : formData.exclude_tags}
                                                        onChange={(val) => handleSelectChange(tagsMode === 'include' ? 'include_tags' : 'exclude_tags', val)}
                                                        placeholder={tagsMode === 'include' ? 'Select tags to include...' : 'Select tags to exclude...'}
                                                        searchInputPlaceholder="Search tags..."
                                                    />
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                                {/* Subject */}
                                                <div className="md:col-span-4 space-y-2">
                                                    <div className="flex items-center justify-between">
                                                        <label className="flex items-center gap-2 text-sm font-semibold text-gray-300">
                                                            <FileText className="w-3 h-3 text-orange-400" />
                                                            Rule Subject
                                                        </label>
                                                        <VariableDropdown
                                                            variables={
                                                                Array.isArray(variables)
                                                                    ? variables
                                                                    : (variables['rundesk'] || variables['rundeskpro'] || variables['ticketing'] || [])
                                                            }
                                                            onSelect={(val) => handleInjectVariable('subject', val)}
                                                            disabled={!formData.event || loadingVariables}
                                                            loading={loadingVariables}
                                                        />
                                                    </div>
                                                    <input
                                                        type="text"
                                                        name="subject"
                                                        value={formData.subject}
                                                        onChange={handleChange}
                                                        placeholder="Summary of the rule..."
                                                        className={inputStyle}
                                                    />
                                                </div>
                                            </div>

                                            {/* Template Dropdown */}
                                            <div className="space-y-3">
                                                <div className="flex items-center justify-between">
                                                    <label className={labelStyle}>
                                                        <FileText className="w-3 h-3 text-orange-400" />
                                                        Event Template
                                                    </label>
                                                    <div className="flex items-center gap-2">
                                                        {formData.description_template?.ticket_open_template && (
                                                            <button
                                                                type="button"
                                                                onClick={() => setPreviewTemplateCode(formData.description_template.ticket_open_template)}
                                                                className="flex items-center gap-1.5 px-2 py-1 bg-cyan-600/10 hover:bg-cyan-600/20 text-cyan-400 border border-cyan-500/20 rounded transition-all text-[10px] font-bold uppercase tracking-widest"
                                                            >
                                                                <Eye size={12} />
                                                                Incident
                                                            </button>
                                                        )}
                                                        {formData.description_template?.ticket_closure_template && (
                                                            <button
                                                                type="button"
                                                                onClick={() => setPreviewTemplateCode(formData.description_template.ticket_closure_template)}
                                                                className="flex items-center gap-1.5 px-2 py-1 bg-emerald-600/10 hover:bg-emerald-600/20 text-emerald-400 border border-emerald-500/20 rounded transition-all text-[10px] font-bold uppercase tracking-widest"
                                                            >
                                                                <Eye size={12} />
                                                                Closure
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                                <SingleSelectLocalSearchApiDropdown
                                                    icon={FileText}
                                                    apiEndpoint="/event_description_templates"
                                                    displayProperty="name"
                                                    searchFields={['name']}
                                                    value={formData.description_template}
                                                    onChange={(item: any) => {
                                                        setFormData(prev => ({ ...prev, description_template: item }));
                                                    }}
                                                    getSecondaryDisplayText={(item: any) => item.is_global ? 'Global' : 'Local'}
                                                    placeholder="Select template..."
                                                    searchInputPlaceholder="Search templates..."
                                                    additionalQueries={{
                                                        query: JSON.stringify({
                                                            ...(level?.name !== 'global' && { "$or": [{ client_id: level?.id }, { is_global: true }] })
                                                        })
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {/* Microsoft Teams Step */}
                                    {showTeamsForm && (
                                        <div className="space-y-5 animate-in fade-in duration-300">
                                            {/* Enable Teams Integration Toggle */}
                                            <div className="flex items-center gap-3 bg-slate-800/30 p-3 rounded-lg border border-gray-800/50">
                                                <span className="text-xs font-bold text-gray-300">Enable Teams Integration:</span>
                                                <button
                                                    type="button"
                                                    onClick={() => setFormData(prev => ({ ...prev, teams_enabled: !prev.teams_enabled }))}
                                                    className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${formData.teams_enabled ? 'bg-emerald-600' : 'bg-gray-700'
                                                        }`}
                                                >
                                                    <span
                                                        className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${formData.teams_enabled ? 'translate-x-4' : 'translate-x-0'
                                                            }`}
                                                    />
                                                </button>
                                                <span className={`text-[11px] font-bold ${formData.teams_enabled ? 'text-emerald-500' : 'text-gray-500'}`}>
                                                    {formData.teams_enabled ? 'Enabled' : 'Disabled'}
                                                </span>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                {/* Channel Dropdown */}
                                                <div className="relative z-[80]">
                                                    <SingleSelectLocalSearchIntegrationApiDropdown
                                                        label="Teams Channel"
                                                        icon={MessageSquare}
                                                        apiEndpoint="integrations/microsoft/channels"
                                                        additionalParams={level.id ? { client_id: level.id } : {}}
                                                        displayProperty="name"
                                                        idProperty="id"
                                                        searchFields={['name']}
                                                        value={formData.teams_channel}
                                                        onChange={(val) => {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                teams_channel: val
                                                            }));
                                                        }}
                                                        placeholder="Select a Channel..."
                                                        searchInputPlaceholder="Search channels..."
                                                    />
                                                </div>
                                            </div>

                                            {/* Open Message Template */}
                                            <div className="space-y-2">
                                                <div className="flex items-center justify-between">
                                                    <label className="flex items-center gap-1.5 text-[10px] font-bold text-white uppercase tracking-wider mb-0.5">
                                                        <FileText className="w-3.5 h-3.5 text-emerald-400" />
                                                        Teams Open Message
                                                    </label>
                                                    <VariableDropdown
                                                        variables={
                                                            Array.isArray(variables)
                                                                ? variables
                                                                : (variables['microsoft_teams'] || variables['teams'] || [])
                                                        }
                                                        onSelect={(val) => handleInjectVariable('teams_open_message', val)}
                                                        disabled={!formData.event || loadingVariables}
                                                        loading={loadingVariables}
                                                    />
                                                </div>
                                                <textarea
                                                    name="teams_open_message"
                                                    value={formData.teams_open_message}
                                                    onChange={handleChange}
                                                    placeholder="Write your Teams open message template here..."
                                                    rows={3}
                                                    className="w-full bg-slate-900 border border-gray-700 rounded-lg px-3 py-3 text-[13px] text-white focus:border-orange-500 focus:bg-gray-800 outline-none transition-all placeholder:text-gray-500 shadow-md custom-scrollbar"
                                                />
                                            </div>

                                            {/* Close Message Template */}
                                            <div className="space-y-2">
                                                <div className="flex items-center justify-between">
                                                    <label className="flex items-center gap-1.5 text-[10px] font-bold text-white uppercase tracking-wider mb-0.5">
                                                        <FileText className="w-3.5 h-3.5 text-emerald-400" />
                                                        Teams Close Message
                                                    </label>
                                                    <VariableDropdown
                                                        variables={
                                                            Array.isArray(variables)
                                                                ? variables
                                                                : (variables['microsoft_teams'] || variables['teams'] || [])
                                                        }
                                                        onSelect={(val) => handleInjectVariable('teams_close_message', val)}
                                                        disabled={!formData.event || loadingVariables}
                                                        loading={loadingVariables}
                                                    />
                                                </div>
                                                <textarea
                                                    name="teams_close_message"
                                                    value={formData.teams_close_message}
                                                    onChange={handleChange}
                                                    placeholder="Write your Teams close message template here..."
                                                    rows={3}
                                                    className="w-full bg-slate-900 border border-gray-700 rounded-lg px-3 py-3 text-[13px] text-white focus:border-orange-500 focus:bg-gray-800 outline-none transition-all placeholder:text-gray-500 shadow-md custom-scrollbar"
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {/* Slack Step */}
                                    {showSlackForm && (
                                        <div className="space-y-5 animate-in fade-in duration-300">
                                            {/* Enable Slack Integration Toggle */}
                                            <div className="flex items-center gap-3 bg-slate-800/30 p-3 rounded-lg border border-gray-800/50">
                                                <span className="text-xs font-bold text-gray-300">Enable Slack Integration:</span>
                                                <button
                                                    type="button"
                                                    onClick={() => setFormData(prev => ({ ...prev, slack_enabled: !prev.slack_enabled }))}
                                                    className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${formData.slack_enabled ? 'bg-emerald-600' : 'bg-gray-700'
                                                        }`}
                                                >
                                                    <span
                                                        className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${formData.slack_enabled ? 'translate-x-4' : 'translate-x-0'
                                                            }`}
                                                    />
                                                </button>
                                                <span className={`text-[11px] font-bold ${formData.slack_enabled ? 'text-emerald-500' : 'text-gray-500'}`}>
                                                    {formData.slack_enabled ? 'Enabled' : 'Disabled'}
                                                </span>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                {/* Channel Dropdown */}
                                                <div className="relative z-[80]">
                                                    <SingleSelectLocalSearchIntegrationApiDropdown
                                                        label="Slack Channel"
                                                        icon={MessageSquare}
                                                        apiEndpoint="integrations/slack/channels"
                                                        displayProperty="name"
                                                        additionalParams={level.id ? { client_id: level.id } : {}}
                                                        idProperty="id"
                                                        searchFields={['name']}
                                                        value={formData.slack_channel}
                                                        onChange={(val) => {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                slack_channel: val
                                                            }));
                                                        }}
                                                        placeholder="Select a Channel..."
                                                        searchInputPlaceholder="Search channels..."
                                                    />
                                                </div>
                                            </div>

                                            {/* Open Message Template */}
                                            <div className="space-y-2">
                                                <div className="flex items-center justify-between">
                                                    <label className="flex items-center gap-1.5 text-[10px] font-bold text-white uppercase tracking-wider mb-0.5">
                                                        <FileText className="w-3.5 h-3.5 text-emerald-400" />
                                                        Slack Open Message
                                                    </label>
                                                    <VariableDropdown
                                                        variables={
                                                            Array.isArray(variables)
                                                                ? variables
                                                                : (variables['slack'] || [])
                                                        }
                                                        onSelect={(val) => handleInjectVariable('slack_open_message', val)}
                                                        disabled={!formData.event || loadingVariables}
                                                        loading={loadingVariables}
                                                    />
                                                </div>
                                                <textarea
                                                    name="slack_open_message"
                                                    value={formData.slack_open_message}
                                                    onChange={handleChange}
                                                    placeholder="Write your Slack open message template here..."
                                                    rows={3}
                                                    className="w-full bg-slate-900 border border-gray-700 rounded-lg px-3 py-3 text-[13px] text-white focus:border-orange-500 focus:bg-gray-800 outline-none transition-all placeholder:text-gray-500 shadow-md custom-scrollbar"
                                                />
                                            </div>

                                            {/* Close Message Template */}
                                            <div className="space-y-2">
                                                <div className="flex items-center justify-between">

                                                    <label className="flex items-center gap-1.5 text-[10px] font-bold text-white uppercase tracking-wider mb-0.5">
                                                        <FileText className="w-3.5 h-3.5 text-emerald-400" />
                                                        Slack Close Message
                                                    </label>
                                                    <VariableDropdown
                                                        variables={
                                                            Array.isArray(variables)
                                                                ? variables
                                                                : (variables['slack'] || [])
                                                        }
                                                        onSelect={(val) => handleInjectVariable('slack_close_message', val)}
                                                        disabled={!formData.event || loadingVariables}
                                                        loading={loadingVariables}
                                                    />
                                                </div>
                                                <textarea
                                                    name="slack_close_message"
                                                    value={formData.slack_close_message}
                                                    onChange={handleChange}
                                                    placeholder="Write your Slack close message template here..."
                                                    rows={3}
                                                    className="w-full bg-slate-900 border border-gray-700 rounded-lg px-3 py-3 text-[13px] text-white focus:border-orange-500 focus:bg-gray-800 outline-none transition-all placeholder:text-gray-500 shadow-md custom-scrollbar"
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {/* Email Step */}
                                    {showEmailForm && (
                                        <div className="space-y-5 animate-in fade-in duration-300">
                                            {/* Enable Email Integration Toggle */}
                                            <div className="flex items-center gap-3 bg-slate-800/30 p-3 rounded-lg border border-gray-800/50">
                                                <span className="text-xs font-bold text-gray-300">Enable Email Notification:</span>
                                                <button
                                                    type="button"
                                                    onClick={() => setFormData(prev => ({ ...prev, email_enabled: !prev.email_enabled }))}
                                                    className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${formData.email_enabled ? 'bg-emerald-600' : 'bg-gray-700'
                                                        }`}
                                                >
                                                    <span
                                                        className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${formData.email_enabled ? 'translate-x-4' : 'translate-x-0'
                                                            }`}
                                                    />
                                                </button>
                                                <span className={`text-[11px] font-bold ${formData.email_enabled ? 'text-emerald-500' : 'text-gray-500'}`}>
                                                    {formData.email_enabled ? 'Enabled' : 'Disabled'}
                                                </span>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                {/* Integration Dropdown */}
                                                <div className="relative z-[80]">
                                                    <label className="text-[10px] font-bold text-gray-500 uppercase tracking-widest block mb-2 px-1">Select Integration</label>
                                                    <select
                                                        value={formData.email_integration_id?.id || formData.email_integration_id?.value || ''}
                                                        onChange={(e) => {
                                                            const selectedOption = emailIntegrations.find(opt => opt.value.toString() === e.target.value);
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                email_integration_id: selectedOption ? { id: selectedOption.value, name: selectedOption.label } : null
                                                            }));
                                                        }}
                                                        className="w-full bg-slate-900 border border-gray-700 rounded-lg px-3 py-3 text-[13px] text-white focus:border-orange-500 focus:bg-gray-800 outline-none transition-all placeholder:text-gray-500 shadow-md"
                                                    >
                                                        <option value="">Select an Email Integration...</option>
                                                        {emailIntegrations.map((opt) => (
                                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                        ))}
                                                    </select>
                                                </div>

                                                {/* Email Template Dropdown */}
                                                <div className="relative z-[80]">
                                                    <SingleSelectLocalSearchApiDropdown
                                                        label="Email Template"
                                                        icon={FileText}
                                                        apiEndpoint={`/event_description_templates${formData.event?.event_type ? `?event_type=${formData.event.event_type}` : ''}`}
                                                        displayProperty="name"
                                                        idProperty="id"
                                                        searchFields={['name']}
                                                        value={formData.email_template}
                                                        onChange={(val) => setFormData(prev => ({ ...prev, email_template: val }))}
                                                        getSecondaryDisplayText={(item: any) => item.is_global ? 'Global' : 'Local'}
                                                        placeholder="Select Template..."
                                                        searchInputPlaceholder="Search templates..."
                                                    />
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div className="space-y-2">
                                                    <label className="flex items-center gap-1.5 text-[10px] font-bold text-white uppercase tracking-wider mb-0.5">
                                                        <MessageSquare className="w-3.5 h-3.5 text-emerald-400" />
                                                        Sender Email
                                                    </label>
                                                    <input
                                                        type="email"
                                                        name="sender_email"
                                                        value={formData.sender_email}
                                                        onChange={handleChange}
                                                        placeholder="no-reply@company.com"
                                                        required
                                                        className="w-full bg-slate-900 border border-gray-700 rounded-lg px-3 py-3 text-[13px] text-white focus:border-orange-500 focus:bg-gray-800 outline-none transition-all placeholder:text-gray-500 shadow-md"
                                                    />
                                                </div>

                                                <div className="space-y-2">
                                                    <label className="flex items-center gap-1.5 text-[10px] font-bold text-white uppercase tracking-wider mb-0.5">
                                                        <Users className="w-3.5 h-3.5 text-emerald-400" />
                                                        Recipient Emails
                                                    </label>
                                                    <input
                                                        type="text"
                                                        name="recipient_emails"
                                                        value={formData.recipient_emails}
                                                        onChange={handleChange}
                                                        placeholder="team@company.com, alerts@company.com"
                                                        className="w-full bg-slate-900 border border-gray-700 rounded-lg px-3 py-3 text-[13px] text-white focus:border-orange-500 focus:bg-gray-800 outline-none transition-all placeholder:text-gray-500 shadow-md"
                                                    />
                                                </div>
                                            </div>

                                            <div className="space-y-2">
                                                <div className="flex items-center justify-between">
                                                    <label className="flex items-center gap-1.5 text-[10px] font-bold text-white uppercase tracking-wider mb-0.5">
                                                        <FileText className="w-3.5 h-3.5 text-emerald-400" />
                                                        Email Subject
                                                    </label>
                                                    <VariableDropdown
                                                        variables={
                                                            Array.isArray(variables)
                                                                ? variables
                                                                : (variables['email'] || variables['smtp'] || [])
                                                        }
                                                        onSelect={(val) => handleInjectVariable('email_subject', val)}
                                                        disabled={!formData.event || loadingVariables}
                                                        loading={loadingVariables}
                                                    />
                                                </div>
                                                <input
                                                    type="text"
                                                    name="email_subject"
                                                    value={formData.email_subject}
                                                    onChange={handleChange}
                                                    placeholder="Alert: {{event.type}} on {{asset.name}}"
                                                    className="w-full bg-slate-900 border border-gray-700 rounded-lg px-3 py-3 text-[13px] text-white focus:border-orange-500 focus:bg-gray-800 outline-none transition-all placeholder:text-gray-500 shadow-md"
                                                />
                                            </div>
                                        </div>
                                    )}

                                </form>
                            </div>

                            <div className="px-6 py-4 border-t border-gray-800 bg-gray-900/50 flex items-center justify-between">
                                <div className="flex-1">
                                    {status.type === 'success' && (
                                        <div className="inline-flex items-center gap-2 text-emerald-400 bg-emerald-400/5 px-3 py-1.5 rounded-lg border border-emerald-400/20 text-[11px] font-semibold animate-in fade-in slide-in-from-left-4">
                                            <CheckCircle2 className="w-4 h-4" />
                                            {status.message}
                                        </div>
                                    )}
                                    {status.type === 'error' && (
                                        <div className="inline-flex items-center gap-2 text-rose-400 bg-rose-400/5 px-3 py-1.5 rounded-lg border border-rose-400/20 text-[11px] font-semibold animate-in fade-in slide-in-from-left-4">
                                            <AlertCircle className="w-4 h-4" />
                                            {status.message}
                                        </div>
                                    )}
                                </div>

                                <div className="flex items-center gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setIsModalOpen(false)}
                                        className="px-4 py-2 rounded-lg text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-800 transition-colors cursor-pointer"
                                    >
                                        Cancel
                                    </button>
                                    {currentStep > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => setCurrentStep(prev => prev - 1)}
                                            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-800 transition-colors border border-gray-700/50 hover:border-gray-600 cursor-pointer"
                                        >
                                            Back
                                        </button>
                                    )}
                                    <button
                                        type="submit"
                                        form="config-form"
                                        disabled={loading}
                                        className={`group flex items-center gap-2 px-6 py-2 rounded-lg bg-orange-600 hover:bg-orange-700 text-white text-[12px] font-bold uppercase tracking-widest transition-all shadow-xl shadow-orange-900/20 active:scale-95 disabled:opacity-50 cursor-pointer`}
                                    >
                                        {loading ? (
                                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        ) : (
                                            currentStep < activeSteps.length ? (
                                                <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                                            ) : (
                                                <Save className="w-4 h-4 group-hover:rotate-12 transition-transform" />
                                            )
                                        )}
                                        {loading
                                            ? 'Saving...'
                                            : (currentStep < activeSteps.length)
                                                ? 'Next Step'
                                                : (editingId ? 'Update Rule' : 'Deploy Rule')
                                        }
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Toggle Status Confirmation Modal */}
            {
                toggleModal.isOpen && (
                    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
                        <div className="bg-gray-900 border border-orange-500/20 rounded-xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col scale-in-center">
                            <div className="p-6">
                                <div className="flex items-center gap-4 mb-4">
                                    <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${toggleModal.isActive ? 'bg-emerald-500/10' : 'bg-rose-500/10'}`}>
                                        <AlertCircle className={`w-6 h-6 ${toggleModal.isActive ? 'text-emerald-500' : 'text-rose-500'}`} />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-bold text-white">
                                            {toggleModal.isActive ? 'Activate Rule' : 'Deactivate Rule'}
                                        </h3>
                                        <p className="text-sm text-gray-400 mt-1">
                                            Are you sure you want to {toggleModal.isActive ? 'activate' : 'deactivate'} this notification rule?
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="px-6 py-4 border-t border-gray-800 bg-gray-900/50 flex items-center justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => setToggleModal({ isOpen: false, id: null, isActive: false })}
                                    disabled={toggling}
                                    className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={confirmToggle}
                                    disabled={toggling}
                                    className={`flex items-center gap-2 px-6 py-2 ${toggleModal.isActive ? 'bg-[#2E8B57] hover:bg-[#267347]' : 'bg-[#B22222] hover:bg-[#8B0000]'
                                        } text-white text-sm font-bold min-w-[120px] justify-center rounded-lg transition-all active:scale-95 disabled:opacity-50`}
                                >
                                    {toggling ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Updating...
                                        </>
                                    ) : (
                                        <>
                                            <CheckCircle2 className="w-4 h-4" />
                                            Confirm
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Custom Delete Confirmation Modal */}
            {
                deleteModal.isOpen && (
                    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
                        <div className="bg-gray-900 border border-red-500/20 rounded-xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col scale-in-center">
                            <div className="p-6">
                                <div className="flex items-center gap-4 mb-4">
                                    <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0">
                                        <AlertCircle className="w-6 h-6 text-red-500" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-bold text-white">Delete Configuration</h3>
                                        <p className="text-sm text-gray-400 mt-1">
                                            Are you sure you want to delete this notification rule? This action cannot be undone.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="px-6 py-4 border-t border-gray-800 bg-gray-900/50 flex items-center justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => setDeleteModal({ isOpen: false, id: null })}
                                    disabled={deleting}
                                    className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={confirmDelete}
                                    disabled={deleting}
                                    className="flex items-center gap-2 px-6 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-bold min-w-[120px] justify-center rounded-lg transition-all shadow-lg shadow-red-900/20 active:scale-95 disabled:opacity-50"
                                >
                                    {deleting ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Deleting...
                                        </>
                                    ) : (
                                        <>
                                            <Trash2 className="w-4 h-4" />
                                            Delete
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {previewTemplateCode && (
                <div className="fixed inset-0 z-[120] flex items-center justify-center p-12 transition-all animate-in fade-in duration-200">
                    <div className="fixed inset-0 bg-black/80 backdrop-blur-md" onClick={() => setPreviewTemplateCode(null)} />
                    <div className="bg-gray-950 border border-white/10 rounded-2xl shadow-[0_0_50px_rgba(0,0,0,0.5)] w-full max-w-5xl h-[85vh] relative flex flex-col overflow-hidden zoom-in-95 border-gray-800">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800/80 bg-gray-900/50">
                            <div className="flex items-center gap-3">
                                <div className="bg-orange-500/20 p-2 rounded-xl border border-orange-500/30">
                                    <Monitor size={20} className="text-orange-400" />
                                </div>
                                <div className="flex flex-col">
                                    <h2 className="text-lg font-bold text-white tracking-tight uppercase">Template Live Preview</h2>
                                    <span className="text-[10px] text-gray-500 font-black uppercase tracking-[0.2em]">High Fidelity Sandboxed Rendering</span>
                                </div>
                            </div>
                            <button
                                onClick={() => setPreviewTemplateCode(null)}
                                className="p-2 text-gray-400 hover:text-white bg-gray-900 border border-gray-800 hover:border-gray-700 rounded-xl transition-all shadow-xl hover:scale-105 active:scale-95"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div className="flex-1 p-6 bg-gray-950/50">
                            <div className="w-full h-full bg-white rounded-xl overflow-hidden shadow-2xl border border-gray-800 ring-4 ring-black/20">
                                <iframe
                                    title="Template Preview"
                                    srcDoc={previewTemplateCode}
                                    className="w-full h-full border-none"
                                />
                            </div>
                        </div>
                        <div className="px-6 py-3 border-t border-gray-800 bg-gray-900/30 flex justify-between items-center">
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Renderer Active</span>
                            </div>
                            <span className="text-[9px] font-mono text-gray-600">ID: PREVIEW_SANDBOX_STABLE</span>
                        </div>
                    </div>
                </div>
            )}
            {/* Uninstalled Integrations Warning Modal */}
            {
                uninstalledModal.isOpen && (
                    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
                        <div className="bg-gray-900 border border-red-500/20 rounded-xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col scale-in-center">
                            <div className="p-6">
                                <div className="flex items-start gap-4 mb-4">
                                    <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0">
                                        <Info className="w-6 h-6 text-red-500 animate-pulse" />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-lg font-bold text-white">Uninstalled Integrations</h3>
                                        <p className="text-xs text-gray-400 mt-2">
                                            The following integration(s) configured for this rule might be uninstalled:
                                        </p>
                                        <div className="mt-3 space-y-2">
                                            {uninstalledModal.integrations.map((integration, index) => (
                                                <div key={index} className="flex items-center gap-2 px-3 py-2 bg-gray-950/40 border border-gray-800 rounded-lg text-xs font-semibold text-gray-200 uppercase tracking-wider">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                                                    {integration === 'rundesk' ? 'Rundesk' : integration === 'microsoft_teams' || integration === 'teams' ? 'Teams' : integration}
                                                </div>
                                            ))}
                                        </div>
                                        <p className="text-xs text-gray-400 mt-4 leading-relaxed">
                                            Please install these integrations again. If you have already reinstalled them, please configure them again.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="px-6 py-4 border-t border-gray-800 bg-gray-900/50 flex items-center justify-end">
                                <button
                                    type="button"
                                    onClick={() => setUninstalledModal({ isOpen: false, integrations: [] })}
                                    className="px-5 py-2 bg-gray-800 hover:bg-gray-750 border border-gray-700 text-gray-300 hover:text-white rounded-lg text-xs font-bold transition-all uppercase tracking-wider cursor-pointer"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }
        </div>
    );
};

export default ConfigurationSettings;
